import { ISKU, SkuQtyRow } from '@admin-panel/types';
import { IOrganization, OrganizationConfigKey } from '@admin-panel/types/lib/org';
import dayjs from 'dayjs';
import JsBarcode from 'jsbarcode';
import jsPDF, { TextOptionsLight } from 'jspdf';

const dpi = 203;
const pointsPerInch = 72; // PDF standard: 72 points per inch
const inchesPerMm = 1 / 25.4; // 1 inch = 25.4 mm
const leftMargin = 3;

const mmToPoints = (mm: number) => {
  return mm * inchesPerMm * pointsPerInch;
};

const addText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  options?: TextOptionsLight,
  fontOptions?: { font?: string; fontWeight?: string; fontSize?: number },
) => {
  let font = fontOptions?.font || 'Helvetica';
  let fontWeight = fontOptions?.fontWeight || 'normal';
  let fontSize = fontOptions?.fontSize || 7;

  doc.setFont(font, fontWeight);
  doc.setFontSize(fontSize);

  doc.text(text, mmToPoints(x), mmToPoints(y), options);
};

const wrapTextWithLimit = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  maxLines: number,
  fontOptions?: { font?: string; fontWeight?: string; fontSize?: number },
) => {
  let font = fontOptions?.font || 'Helvetica';
  let fontWeight = fontOptions?.fontWeight || 'normal';
  let fontSize = fontOptions?.fontSize || 7;

  doc.setFont(font, fontWeight);
  doc.setFontSize(fontSize);

  const words = text.split(' ');
  let line = '';
  let lines = [];

  for (let i = 0; i < words.length; i++) {
    const tempLine = line + words[i] + ' ';
    const tempLineWidth = doc.getTextWidth(tempLine) * 1.1;

    if (tempLineWidth > maxWidth && i > 0) {
      lines.push(line.trim());
      if (lines.length === maxLines) {
        // If we've reached the maximum number of lines, stop processing
        break;
      }
      line = words[i] + ' ';
    } else {
      line = tempLine;
    }
  }

  // Add the last line if we haven't reached the maximum
  if (line.trim() && lines.length < maxLines) {
    lines.push(line.trim());
  }

  // Truncate to maxLines if necessary
  lines = lines.slice(0, maxLines);

  // Draw the lines
  lines.forEach((line, index) => {
    doc.text(line, mmToPoints(x), mmToPoints(y + index * 3));
  });
};

const createLabelPdf = (format: string) => {
  let labelWidthMm = 51;
  let labelHeightMm = 101;
  console.log('Doc format', format);
  if (format === 'jewellery') {
    labelWidthMm = 96;
    labelHeightMm = 12;
  }

  // Convert mm to points
  const widthPt = labelWidthMm * inchesPerMm * pointsPerInch;
  const heightPt = labelHeightMm * inchesPerMm * pointsPerInch;

  // Calculate the scaling factor
  const scaleFactor = dpi / pointsPerInch;

  // Create a new jsPDF instance
  const doc = new jsPDF({
    unit: 'pt',
    format: [widthPt, heightPt],
    hotfixes: ['px_scaling'],
    orientation: format === 'jewellery' ? 'l' : 'p',
  });

  // Set the PDF scaling to match 203 DPI
  doc.internal.scaleFactor = scaleFactor;

  return doc;
};

const normalizeLabelFormatToken = (value?: string) => value?.trim().toLowerCase() ?? '';

const shouldUseJewelleryLabelFormat = (skuEntry?: ISKU) => {
  if (!skuEntry) {
    return false;
  }

  const productParentName = normalizeLabelFormatToken(skuEntry.productType?.parent?.name);
  const productTypeName = normalizeLabelFormatToken(skuEntry.productType?.name);

  return (
    productParentName === 'jewellery' ||
    productTypeName === 'lingerie accessories' ||
    productTypeName === 'cufflinks' ||
    productTypeName === 'rakhi & lumbas'
  );
};

const generateLabelPageForApparelAndBag = (
  doc: jsPDF,
  skuEntry: ISKU,
  organization: IOrganization,
  format: string,
) => {
  let nextSlot = 5;
  addText(doc, 'Style Code:  ' + skuEntry.vendorArticleNumber, leftMargin, nextSlot);
  nextSlot += 3.5;
  wrapTextWithLimit(doc, 'Style: ' + skuEntry.labelName, leftMargin, nextSlot, 46, 2);
  nextSlot += 7;
  //addText(doc, 'BRAND: ' + skuEntry.brandName, leftMargin, nextSlot);
  wrapTextWithLimit(doc, 'BRAND: ' + skuEntry.brandName, leftMargin, nextSlot, 46, 1);
  nextSlot += 3.5;
  addText(doc, 'Size: ' + skuEntry.size, leftMargin, nextSlot, undefined, {
    fontWeight: 'bold',
  });
  nextSlot += 3.5;
  addText(doc, 'Color: ' + skuEntry.brandColor, leftMargin, nextSlot, undefined, {
    fontWeight: 'bold',
  });
  nextSlot += 3.5;

  addText(doc, 'Product: ' + skuEntry.productType.name, leftMargin, nextSlot);
  nextSlot += 3.5;
  addText(doc, 'Pack contains: ' + skuEntry.components, leftMargin, nextSlot);
  nextSlot += 3.5;
  addText(doc, 'Quantity: ' + skuEntry.componentsQty, leftMargin, nextSlot);
  nextSlot += 3.5;

  addText(doc, 'Country Of Origin: ' + skuEntry.countryOfOrigin, leftMargin, nextSlot);

  nextSlot += 3.5;
  if (format == 'apparel') {
    addText(doc, 'Wash Type: ' + skuEntry.washType, leftMargin, nextSlot);
  }

  nextSlot += 3.5;
  if (format == 'apparel') {
    addText(doc, 'Fabric: ' + skuEntry.fabricForLabel, leftMargin, nextSlot);
  } else {
    addText(doc, 'Material: ' + skuEntry.materialForLabelPrint, leftMargin, nextSlot);
  }

  nextSlot += 5;

  if (skuEntry.importedOn) {
    addText(
      doc,
      'Imported On: ' + dayjs(skuEntry.importedOn).format('MMM YYYY'),
      leftMargin,
      nextSlot,
      undefined,
      {
        fontWeight: 'normal',
        fontSize: 6,
      },
    );
  }
  nextSlot += 3;
  if (skuEntry.importedOn) {
    addText(doc, 'Marketed & Imported By:', leftMargin, nextSlot, undefined, {
      fontWeight: 'bold',
      fontSize: 6,
    });
  } else {
    addText(doc, 'Marketed By:', leftMargin, nextSlot, undefined, {
      fontWeight: 'bold',
      fontSize: 6,
    });
  }
  nextSlot += 3;
  addText(
    doc,
    '' + organization.config[OrganizationConfigKey.GST_NAME],
    leftMargin,
    nextSlot,
    undefined,
    {
      fontWeight: 'bold',
      fontSize: 5,
    },
  );
  nextSlot += 3;

  wrapTextWithLimit(
    doc,
    organization.config[OrganizationConfigKey.GST_ADDRESS],
    leftMargin,
    nextSlot,
    46,
    3,
    { fontWeight: 'normal', fontSize: 5 },
  );
  nextSlot += 7;
  // next slot has reached 73

  addText(doc, 'Customer Care:', leftMargin, nextSlot, undefined, {
    fontWeight: 'bold',
    fontSize: 6,
  });
  nextSlot += 3;
  addText(
    doc,
    'Email: ' + organization.config[OrganizationConfigKey.CC_EMAIL],
    leftMargin,
    nextSlot,
    undefined,
    {
      fontWeight: 'normal',
      fontSize: 6,
    },
  );
  nextSlot += 3;
  addText(
    doc,
    'Ph: ' + organization.config[OrganizationConfigKey.CC_PHONE],
    leftMargin,
    nextSlot,
    undefined,
    {
      fontWeight: 'normal',
      fontSize: 6,
    },
  );

  nextSlot += 7;

  const newLabel = false; //skuEntry.importedOn && newPrice > oldPrice;
  if (newLabel) {
    // Use newMrp if available, otherwise fall back to mrp
    const displayPrice = skuEntry.newMrp || skuEntry.mrp;
    const oldPriceText = 'Rs ' + skuEntry.mrp + '/-';
    addText(doc, oldPriceText, leftMargin + 3, nextSlot, undefined, {
      fontSize: 10,
      fontWeight: 'normal',
    });

    // Add strikethrough line
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9);
    const textWidth = doc.getTextWidth(oldPriceText);
    doc.line(
      mmToPoints(leftMargin + 2.5),
      mmToPoints(nextSlot - 1),
      mmToPoints(leftMargin + textWidth + 5),
      mmToPoints(nextSlot - 1),
    );

    addText(doc, 'Rs ' + displayPrice + '/-', 25, nextSlot, undefined, {
      fontSize: 10,
      fontWeight: 'bold',
    });
  } else {
    const displayPrice = skuEntry.newMrp || skuEntry.mrp;
    addText(
      doc,
      'Rs ' + displayPrice + '/-',
      17,
      nextSlot,
      {
        align: 'center',
      },
      {
        fontSize: 14,
        fontWeight: 'bold',
      },
    );
  }

  nextSlot += 3;
  addText(
    doc,
    newLabel ? '(GST Revised) (Inclusive of all Taxes)' : '(Inclusive of all Taxes)',
    newLabel ? 13 : 18,
    nextSlot,
    {
      align: 'center',
    },
    { fontSize: 5 },
  );

  nextSlot += 4;

  const canvas = document.createElement('canvas');
  JsBarcode(canvas, `${skuEntry.barcode}`, {
    format: 'CODE128',
    displayValue: true,
  });
  const imgData = canvas.toDataURL('image/jpeg');
  doc.addImage(imgData, 'JPEG', 0, mmToPoints(nextSlot), mmToPoints(50), mmToPoints(15));
};

const generateLabelPageForJewelry = (doc: jsPDF, skuEntry: ISKU) => {
  let nextSlot = 2;
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, `${skuEntry.barcode}`, {
    format: 'CODE128',
    displayValue: true,
  });
  const imgData = canvas.toDataURL('image/jpeg');
  doc.addImage(
    imgData,
    'JPEG',
    mmToPoints(45),
    mmToPoints(nextSlot - 2),
    mmToPoints(50),
    mmToPoints(9),
  );
  nextSlot += 9;
  addText(doc, skuEntry.labelName, 50, nextSlot, undefined, {
    fontSize: 4,
  });
  // Use newMrp if available, otherwise fall back to mrp
  const displayPrice = skuEntry.newMrp || skuEntry.mrp;
  addText(doc, 'Rs ' + displayPrice + '/-', 80, nextSlot, undefined, {
    fontWeight: 'bold',
    fontSize: 5,
  });
};

export const generateProductLabelFileWithNewMrp = (
  skuQtyRows: Array<SkuQtyRow>,
  organization: IOrganization,
) => {
  const firstSkuEntry = skuQtyRows[0]?.skuEntry;
  const format = shouldUseJewelleryLabelFormat(firstSkuEntry) ? 'jewellery' : 'long';
  const doc = createLabelPdf(format);

  for (let i = 0; i < skuQtyRows.length; i++) {
    const skuEntry: ISKU | undefined = skuQtyRows[i].skuEntry;
    if (skuEntry && skuEntry?.id) {
      for (let j = 0; j < skuQtyRows[i].quantity; j++) {
        if (i > 0 || j > 0) doc.addPage();

        if (format == 'jewellery') {
          generateLabelPageForJewelry(doc, skuEntry);
        } else {
          if (
            skuEntry.productType.parent.parent.name.toLowerCase() == 'apparel' ||
            skuEntry.productType.parent.parent.name.toLowerCase() == 'clothing' ||
            skuEntry.productType.parent.parent.name.toLowerCase() == 'topwear'
          ) {
            generateLabelPageForApparelAndBag(doc, skuEntry, organization, 'apparel');
          } else if (skuEntry.productType.parent.name.toLowerCase() == 'bags') {
            generateLabelPageForApparelAndBag(doc, skuEntry, organization, 'bags');
          } else {
            generateLabelPageForApparelAndBag(doc, skuEntry, organization, 'bags');
          }
        }
      }
    }
  }
  return doc;
};
